import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LabelList } from 'recharts';
import { getDashboardStats, getDashboardTrend, getMonthlyData, getRecentServices, getRevenueReport, getServiceRevenueRanking, getFinancialSummary, getStore, getStores } from '../api';

function Dashboard({ systemName = '居家健康管理系统' }) {
  const [stats, setStats] = useState({});
  const [trendData, setTrendData] = useState([]);
  const [storeName, setStoreName] = useState('');
  const [monthlyData, setMonthlyData] = useState([]);
  const [recentServices, setRecentServices] = useState([]);
  const [revenueReport, setRevenueReport] = useState({});
  const [serviceRanking, setServiceRanking] = useState([]);
  const [financialSummary, setFinancialSummary] = useState({});
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  const [user] = useState(() => {
    const stored = sessionStorage.getItem('health_user');
    return stored ? JSON.parse(stored) : null;
  });

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const storeIdParam = isAdmin ? undefined : user?.storeId;

        // 获取门店名称
        if (user?.storeId) {
          try {
            const storeRes = await getStore(user.storeId);
            if (storeRes.data.success) {
              setStoreName(storeRes.data.data.name || '');
            }
          } catch (e) { console.error(e); }
        }

        const [statsRes, trendRes, monthlyRes, servicesRes, revenueRes, rankingRes, financeRes, storesRes] = await Promise.all([
          getDashboardStats(storeIdParam),
          getDashboardTrend(),
          getMonthlyData(),
          getRecentServices(),
          getRevenueReport(),
          getServiceRevenueRanking(),
          getFinancialSummary(),
          getStores()
        ]);
        // 处理stats数据，映射API返回的字段
        if (statsRes.data.success) {
          const apiData = statsRes.data.data;
          // 计算本月营收和订单（从monthlyData中获取当前月数据）
          const currentMonth = new Date().getMonth() + 1;
          const monthData = monthlyRes.data.data?.find(m => m.month === currentMonth) || {};
          const monthIncome = monthData.totalAmount || 0;
          const monthOrderCount = monthData.totalCount || 0;
          // 计算完单率
          const processingCount = apiData.processingCount || 0;
          const completedCount = apiData.completedCount || 0;
          const totalCount = processingCount + completedCount;
          const completionRate = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

          setStats({
            ...apiData,
            // 映射API返回的字段到前端期望的字段
            storeCount: apiData.storeCount || (storesRes.data.data?.length) || 0,
            managerCount: apiData.storeCount || 0,
            orderCount: apiData.orderCount || apiData.todayServiceCount || 0,
            monthIncome: monthIncome,
            monthOrderCount: monthOrderCount,
            newCustomers: apiData.newCustomers || 0,
            completionRate: completionRate
          });
        }
        if (trendRes.data.success) setTrendData(trendRes.data.data);
        if (monthlyRes.data.success) setMonthlyData(monthlyRes.data.data);
        if (servicesRes.data.success) setRecentServices(servicesRes.data.data);
        if (revenueRes.data.success) setRevenueReport(revenueRes.data.data);
        if (rankingRes.data.success) setServiceRanking(rankingRes.data.data);
        if (financeRes.data.success) setFinancialSummary(financeRes.data.data);
        if (storesRes.data.success) setStores(storesRes.data.data);
      } catch (error) { console.error(error); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [isAdmin, user?.storeId]);

  const getStatusBadge = (status) => {
    const map = { pending: '待处理', processing: '进行中', completed: '已完成', cancelled: '已取消' };
    const cls = { pending: 'badge-yellow', processing: 'badge-gray', completed: 'badge-green', cancelled: 'badge-red' };
    return <span className={`status-badge ${cls[status]}`}>{map[status] || status}</span>;
  };

  if (loading) return <div className="loading-text">加载中...</div>;

  const hour = new Date().getHours();
  const greet = hour < 12 ? '早上好' : hour < 18 ? '下午好' : '晚上好';

  // 环形图数据
  const targetData = [
    { name: '已完成', value: stats.monthIncome || 0 },
    { name: '剩余', value: Math.max(0, (stats.monthlyTarget || 10000) - (stats.monthIncome || 0)) }
  ];
  const COLORS = ['#1890ff', '#e8e8e8'];

  return (
    <div>
      {/* 欢迎语和工作台标题 */}
      <div className="content-card">
        <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
          {systemName}-工作台
        </div>
        <div style={{ fontSize: '18px', fontWeight: '500', color: '#666' }}>
          {greet}，{user?.nickname || user?.username || '用户'}店长
        </div>
        {user?.storeId && storeName && (
          <div style={{ fontSize: '16px', color: '#999', marginTop: '4px' }}>
            {storeName}
          </div>
        )}
      </div>

      {isAdmin ? (
        /* Admin工作台 - 显示所有门店汇总数据 */
        <>
          <div className="content-card">
            <div className="section-title">数据概览</div>
            <div className="stats-cards">
              <div className="stats-row">
                <div className="stat-box">
                  <div className="stat-label">门店总数</div>
                  <div className="stat-value">{stats.storeCount || 0}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">店长总数</div>
                  <div className="stat-value">{stats.managerCount || 0}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">客户总数</div>
                  <div className="stat-value">{stats.customerCount || 0}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">订单总数</div>
                  <div className="stat-value">{stats.orderCount || 0}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="content-card">
            <div className="section-title">运营概览</div>
            <div className="stats-cards">
              <div className="stats-row">
                <div className="stat-box">
                  <div className="stat-label">本月营收</div>
                  <div className="stat-value green">¥{(stats.monthIncome || 0).toLocaleString()}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">本月订单</div>
                  <div className="stat-value">{stats.monthOrderCount || 0}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">新增客户</div>
                  <div className="stat-value">{stats.newCustomers || 0}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">完单率</div>
                  <div className="stat-value pink">{stats.completionRate || 0}%</div>
                </div>
              </div>
            </div>
          </div>

          <div className="content-card">
            <div className="section-title">门店列表</div>
            <table className="basic-table">
              <thead>
                <tr>
                  <th>序号</th>
                  <th>门店名称</th>
                  <th>店铺营收</th>
                  <th>店铺订单</th>
                  <th>创建日期</th>
                </tr>
              </thead>
              <tbody>
                {(stores || []).map((store, i) => (
                  <tr key={store.id}>
                    <td>{i+1}</td>
                    <td>{store.name}</td>
                    <td>¥{store.income || 0}</td>
                    <td>{store.orderCount || 0}</td>
                    <td>{store.created_at ? store.created_at.split(' ')[0] : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {financialSummary.todayIncome !== undefined && (
            <div className="content-card">
              <div className="section-title">财务汇总</div>
              <div className="stats-cards">
                <div className="stats-row">
                  <div className="stat-box">
                    <div className="stat-label">今日收入</div>
                    <div className="stat-value green">¥{(financialSummary.todayIncome || 0).toLocaleString()}</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-label">本月收入</div>
                    <div className="stat-value green">¥{(financialSummary.monthIncome || 0).toLocaleString()}</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-label">退款总额</div>
                    <div className="stat-value red">¥{(financialSummary.totalRefund || 0).toLocaleString()}</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-label">完单率</div>
                    <div className="stat-value">{financialSummary.completionRate || 0}%</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Manager/单店工作台 */
        <>
          <div className="content-card">
            <div className="section-title">服务统计</div>
            <div className="stats-cards">
              <div className="stats-row">
                <div className="stat-box">
                  <div className="stat-label">今日营收</div>
                  <div className="stat-value">
                    ¥{stats.todayIncome || 0}
                    {stats.incomeChange !== undefined && stats.incomeChange !== null && (
                      <span style={{ fontSize: '12px', marginLeft: '8px', color: stats.incomeChange >= 0 ? '#52c41a' : '#f5222d' }}>
                        {stats.incomeChange > 0 ? '↑' : stats.incomeChange < 0 ? '↓' : '-'} {Math.abs(stats.incomeChange)}%
                      </span>
                    )}
                  </div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">今日服务次数</div>
                  <div className="stat-value">
                    {stats.todayServiceCount || stats.todayOrderCount || 0}
                    {stats.serviceCountChange !== undefined && stats.serviceCountChange !== null && (
                      <span style={{ fontSize: '12px', marginLeft: '8px', color: stats.serviceCountChange >= 0 ? '#52c41a' : '#f5222d' }}>
                        {stats.serviceCountChange > 0 ? '↑' : stats.serviceCountChange < 0 ? '↓' : '-'} {Math.abs(stats.serviceCountChange)}%
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="stats-row">
                <div className="stat-box">
                  <div className="stat-label">客户总数</div>
                  <div className="stat-value">
                    {stats.customerCount || 0}
                    {stats.customerCountChange !== undefined && stats.customerCountChange !== null && (
                      <span style={{ fontSize: '12px', marginLeft: '8px', color: stats.customerCountChange >= 0 ? '#52c41a' : '#f5222d' }}>
                        {stats.customerCountChange > 0 ? '↑' : stats.customerCountChange < 0 ? '↓' : '-'} {Math.abs(stats.customerCountChange)}%
                      </span>
                    )}
                  </div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">活跃客户</div>
                  <div className="stat-value">
                    {stats.activeCustomers || 0}
                    {stats.activeCustomersChange !== undefined && stats.activeCustomersChange !== null && (
                      <span style={{ fontSize: '12px', marginLeft: '8px', color: stats.activeCustomersChange >= 0 ? '#52c41a' : '#f5222d' }}>
                        {stats.activeCustomersChange > 0 ? '↑' : stats.activeCustomersChange < 0 ? '↓' : '-'} {Math.abs(stats.activeCustomersChange)}%
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="content-card">
            <div className="section-title">订单状态</div>
            <div className="stats-cards">
              <div className="stats-row">
                <div className="stat-box">
                  <div className="stat-label">正在进行</div>
                  <div className="stat-value orange">
                    {stats.processingCount || 0}
                    {stats.processingCountChange !== undefined && stats.processingCountChange !== null && (
                      <span style={{ fontSize: '12px', marginLeft: '8px', color: stats.processingCountChange >= 0 ? '#52c41a' : '#f5222d' }}>
                        {stats.processingCountChange > 0 ? '↑' : stats.processingCountChange < 0 ? '↓' : '-'} {Math.abs(stats.processingCountChange)}%
                      </span>
                    )}
                  </div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">已完成</div>
                  <div className="stat-value green">
                    {stats.completedCount || 0}
                    {stats.completedCountChange !== undefined && stats.completedCountChange !== null && (
                      <span style={{ fontSize: '12px', marginLeft: '8px', color: stats.completedCountChange >= 0 ? '#52c41a' : '#f5222d' }}>
                        {stats.completedCountChange > 0 ? '↑' : stats.completedCountChange < 0 ? '↓' : '-'} {Math.abs(stats.completedCountChange)}%
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 营收趋势图 */}
          {trendData.length > 0 && (
            <div className="content-card">
              <div className="section-title">营收趋势（最近7天）</div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={trendData} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(value) => value.slice(5)} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => [`¥${value}`, '营收']} />
                  <Line type="monotone" dataKey="orderAmount" stroke="#1890ff" strokeWidth={2} dot={{ fill: '#1890ff', r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}


          {revenueReport.list && revenueReport.list.length > 0 && (
            <div className="content-card">
              <div className="section-title">月度收入报表</div>
              <table className="basic-table">
                <thead>
                  <tr><th>月份</th><th>订单数</th><th>收入</th><th>退款</th><th>净收入</th></tr>
                </thead>
                <tbody>
                  {revenueReport.list.slice(0, 6).map(r => (
                    <tr key={r.month}>
                      <td>{r.month}</td>
                      <td>{r.orderCount}</td>
                      <td className="green">¥{r.revenue}</td>
                      <td className="red">¥{r.refund}</td>
                      <td className="green">¥{r.netRevenue}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {revenueReport.totals && (
                <div style={{ marginTop: 12, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                  合计: 订单 {revenueReport.totals.orderCount} | 收入 ¥{revenueReport.totals.revenue} | 退款 ¥{revenueReport.totals.refund} | 净收入 ¥{revenueReport.totals.netRevenue}
                </div>
              )}
            </div>
          )}

          {serviceRanking.length > 0 && (() => {
            const totalRevenue = serviceRanking.reduce((sum, s) => sum + (s.totalRevenue || 0), 0);
            return (
              <div className="content-card">
                <div className="section-title">收入排行</div>
                <table className="basic-table">
                  <thead>
                    <tr><th>排名</th><th>服务项目</th><th>订单数</th><th>总收入</th><th>占比</th></tr>
                  </thead>
                  <tbody>
                    {serviceRanking.map(s => {
                      const revenue = s.totalRevenue || 0;
                      const percent = totalRevenue > 0 ? Math.round((revenue / totalRevenue) * 100) : 0;
                      return (
                        <tr key={s.rank}>
                          <td>{s.rank}</td>
                          <td>{s.name}</td>
                          <td>{s.orderCount}次</td>
                          <td className="green">¥{revenue}</td>
                          <td>{percent}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}

          <div className="data-table">
            <div className="section-title">服务记录</div>
            <table className="basic-table">
              <thead>
                <tr>
                  <th className="no-cell">序号</th>
                  <th>客户</th>
                  <th>服务项目</th>
                  <th>金额</th>
                  <th>日期</th>
                  <th>状态</th>
                </tr>
              </thead>
              <tbody>
                {recentServices.length > 0 ? recentServices.map((s, i) => (
                  <tr key={s.id}>
                    <td className="no-cell">{i + 1}</td>
                    <td>{s.customer_name}</td>
                    <td>{s.service_name}</td>
                    <td>¥{s.amount}</td>
                    <td>{s.record_date}</td>
                    <td>{s.completion_rate === 100 ? '已完成' : '进行中'}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="7" style={{ textAlign: 'center', color: '#999' }}>暂无数据</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default Dashboard;